import React, { useMemo, useState } from 'react';
import { Button } from './ui/button';
import { stripeConnectStatus, stripeConnectOnboard } from '../lib/api';
import { CheckCircle2, Circle, ChevronRight, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Onboarding checklist that lives at the top of the TrainerDashboard.
 * Computes completion percentage from the current user document and shows the
 * trainer exactly what is left to do before they can take bookings.
 *
 * When everything is 100% done, the component collapses itself to a tiny
 * "You're all set" pill (and the trainer can dismiss it permanently via
 * localStorage).
 */
const TrainerSetupChecklist = ({ user, onOpenEditProfile }) => {
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem('doorguard_setup_dismissed') === '1';
  });

  const profile = user?.trainer_profile || {};

  const items = useMemo(() => {
    const photoOk = !!profile.profile_image;
    const bioOk = !!(profile.bio && profile.bio.trim().length >= 30);
    const allPricesOk =
      Number(profile.price_personal_training) > 0 &&
      Number(profile.price_self_defense) > 0 &&
      Number(profile.price_jiu_jitsu) > 0;
    const locationOk = !!(user?.city && profile.state);
    const agreementOk = !!profile.trainer_agreement_accepted_at;
    const stripeOk = !!profile.stripe_charges_enabled;

    return [
      {
        key: 'photo',
        done: photoOk,
        title: 'Add a profile photo',
        body: 'Clients are 3× more likely to book trainers with a clear photo.',
        action: 'Edit profile',
        onAction: onOpenEditProfile,
      },
      {
        key: 'bio',
        done: bioOk,
        title: 'Write a bio (30+ characters)',
        body: 'Tell clients who you train, what you specialize in, and what they can expect.',
        action: 'Edit profile',
        onAction: onOpenEditProfile,
      },
      {
        key: 'prices',
        done: allPricesOk,
        title: 'Set a price for each service',
        body: 'Personal Training, Self-Defense, and Jiu-Jitsu each need a price (or leave blank to not offer that service, but at least one is required).',
        action: 'Set prices',
        onAction: onOpenEditProfile,
      },
      {
        key: 'location',
        done: locationOk,
        title: 'Add your city &amp; state',
        body: 'Required to appear on the map. We use it only to pin you for nearby clients, we never publish your exact address.',
        action: 'Add location',
        onAction: onOpenEditProfile,
      },
      {
        key: 'agreement',
        done: agreementOk,
        title: 'Accept the Independent Contractor Agreement',
        body: 'Required before your account can accept bookings.',
        action: 'Open agreement',
        onAction: () => window.location.reload(), // modal auto-opens on dashboard load
      },
      {
        key: 'stripe',
        done: stripeOk,
        title: 'Connect a bank account (Stripe)',
        body: 'How you actually get paid. Takes ~2 minutes. You keep 80% of every session.',
        action: 'Connect Stripe',
        onAction: async () => {
          try {
            const res = await stripeConnectStatus().catch(() => null);
            // If already partially connected, jump straight to onboarding link
            const r = await stripeConnectOnboard();
            if (r.data?.url) window.location.href = r.data.url;
            else toast.error('Could not start Stripe onboarding');
            // Suppress unused warning
            void res;
          } catch (e) {
            const msg = e?.response?.data?.detail || 'Could not start Stripe onboarding';
            toast.error(typeof msg === 'string' ? msg : 'Stripe error');
          }
        },
      },
    ];
  }, [user, profile, onOpenEditProfile]);

  const total = items.length;
  const done = items.filter((i) => i.done).length;
  const pct = Math.round((done / total) * 100);
  const allDone = done === total;

  if (dismissed && allDone) return null;

  if (allDone) {
    return (
      <div
        className="mb-6 flex items-center justify-between bg-[#18181B] border border-emerald-500/30 rounded-lg px-4 py-3"
        data-testid="setup-checklist-complete"
      >
        <div className="flex items-center gap-2 text-sm">
          <Sparkles className="w-4 h-4 text-emerald-400" />
          <span className="text-emerald-300 font-semibold">You&apos;re all set.</span>
          <span className="text-zinc-400">Your profile is ready to accept bookings.</span>
        </div>
        <button
          onClick={() => {
            localStorage.setItem('doorguard_setup_dismissed', '1');
            setDismissed(true);
          }}
          className="text-zinc-500 hover:text-zinc-200 p-1"
          aria-label="Dismiss"
          data-testid="setup-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <section
      className="mb-8 bg-[#18181B] border border-zinc-800 rounded-lg overflow-hidden"
      data-testid="trainer-setup-checklist"
    >
      {/* Header / progress */}
      <div className="px-6 pt-6 pb-5 border-b border-zinc-800">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[#C0A062] font-semibold mb-1">
              Get ready to take bookings
            </p>
            <h2 className="text-2xl font-bold text-white">Finish setting up your profile</h2>
            <p className="text-sm text-zinc-400 mt-1">
              {done} of {total} complete. Your profile is hidden from clients until every step is done.
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-[#C0A062] leading-none" data-testid="setup-pct">
              {pct}%
            </p>
          </div>
        </div>

        <div className="mt-4 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#C0A062] transition-all duration-500 ease-out"
            style={{ width: `${pct}%` }}
            data-testid="setup-progress-bar"
          />
        </div>
      </div>

      {/* Items */}
      <ul className="divide-y divide-zinc-800">
        {items.map((it) => (
          <li
            key={it.key}
            className={`px-6 py-4 flex items-start gap-4 ${it.done ? 'opacity-60' : ''}`}
            data-testid={`setup-item-${it.key}`}
          >
            <div className="shrink-0 mt-0.5">
              {it.done ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              ) : (
                <Circle className="w-5 h-5 text-zinc-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-semibold ${it.done ? 'line-through text-zinc-500' : 'text-white'}`}
                dangerouslySetInnerHTML={{ __html: it.title }}
              />
              {!it.done && <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{it.body}</p>}
            </div>
            {!it.done && (
              <Button
                variant="ghost"
                size="sm"
                onClick={it.onAction}
                className="shrink-0 text-[#C0A062] hover:bg-[#C0A062]/10 hover:text-[#C0A062]"
                data-testid={`setup-action-${it.key}`}
              >
                {it.action}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default TrainerSetupChecklist;
